/**
 * useWebSocket — Hook para conexión en tiempo real con el backend.
 *
 * Reconexión automática con backoff exponencial.
 * Multiplexado: un solo WS por tenant, eventos filtrados por run_id en cliente.
 * El store recibe eventos vía handleWSEvent.
 */
import { useEffect, useRef, useCallback } from 'react';
import { useAppStore, handleWSEvent } from '../store';
import type { WSEvent } from '../types';

// WS URL segun el entorno:
//  - Producción/nube (HTTPS): wss:// al MISMO host, sin puerto → Traefik enruta
//    /api/v1/executions/ws al backend (evita "Mixed Content" y el puerto 8000).
//  - Dev local (HTTP): ws:// directo al backend en :8000.
const WS_URL =
  window.location.protocol === 'https:'
    ? `wss://${window.location.host}/api/v1/executions/ws`
    : `ws://${window.location.hostname}:8000/api/v1/executions/ws`;
const MAX_RETRIES = 10;
const BASE_DELAY_MS = 1000;

export function useWebSocket(token: string | null) {
  const wsRef = useRef<WebSocket | null>(null);
  const retriesRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const store = useAppStore;

  const connect = useCallback(() => {
    if (!token) return;
    const ws = new WebSocket(`${WS_URL}?token=${token}`);
    wsRef.current = ws;

    ws.onopen = () => {
      retriesRef.current = 0;
      store.getState().addNotification({
        type: 'info',
        title: 'Conectado',
        message: 'Actualizaciones en tiempo real activas',
      });
    };

    ws.onmessage = (e: MessageEvent) => {
      try {
        const event: WSEvent = JSON.parse(e.data);
        handleWSEvent(event, store);
      } catch {
        // Ignorar mensajes malformados
      }
    };

    ws.onclose = (e) => {
      if (e.code === 1000) return; // Cierre limpio
      if (retriesRef.current >= MAX_RETRIES) return;

      const delay = BASE_DELAY_MS * Math.pow(2, retriesRef.current);
      retriesRef.current += 1;
      timerRef.current = setTimeout(connect, delay);
    };

    ws.onerror = () => {
      ws.close();
    };
  }, [token, store]);

  useEffect(() => {
    connect();
    return () => {
      clearTimeout(timerRef.current);
      wsRef.current?.close(1000);
    };
  }, [connect]);

  const send = useCallback((data: unknown) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(data));
    }
  }, []);

  return { send };
}
